/**
 * Prevents third-party scripts (e.g. EasySell COD) from replacing the cart
 * when customers browse other product pages.
 */
(function () {
  if (window.sfCartGuard) return;

  var bypassDepth = 0;

  function isBypassed() {
    return bypassDepth > 0;
  }

  function withThemeBypass(fn) {
    bypassDepth += 1;
    var result;

    try {
      result = fn();
    } catch (err) {
      bypassDepth -= 1;
      throw err;
    }

    // Return jQuery deferreds as-is so callers can use .done() / .fail().
    if (result && typeof result.always === "function") {
      return result.always(function () {
        bypassDepth -= 1;
      });
    }

    return Promise.resolve(result).finally(function () {
      bypassDepth -= 1;
    });
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(update|change)(\.js|\.json)?(\?|$)/.test(
      String(url || ""),
    );
  }

  function isCartClearUrl(url) {
    return /\/cart\/clear(\.js|\.json)?(\?|$)/.test(String(url || ""));
  }

  function isCartAddUrl(url) {
    return /\/cart\/add(\.js|\.json)?(\?|$)/.test(String(url || ""));
  }

  function snapshotItems(items) {
    return (items || []).map(function (item) {
      return {
        variant_id: item.variant_id,
        product_id: item.product_id,
        quantity: item.quantity,
        selling_plan: item.selling_plan_allocation
          ? item.selling_plan_allocation.selling_plan.id
          : null,
      };
    });
  }

  function getRemovedItems(beforeItems, afterItems) {
    var afterProductIds = {};
    (afterItems || []).forEach(function (item) {
      afterProductIds[item.product_id] = true;
    });

    return (beforeItems || []).filter(function (item) {
      return !afterProductIds[item.product_id];
    });
  }

  function shouldRestore(beforeItems, afterItems) {
    return getRemovedItems(beforeItems, afterItems).length > 0;
  }

  function fetchCart() {
    return nativeFetch("/cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    }).then(function (response) {
      return response.json();
    });
  }

  function restoreSnapshot(beforeItems) {
    if (!beforeItems.length) {
      return withThemeBypass(function () {
        return nativeFetch("/cart/clear.js", {
          method: "POST",
          credentials: "same-origin",
        });
      });
    }

    return withThemeBypass(function () {
      return nativeFetch("/cart/clear.js", {
        method: "POST",
        credentials: "same-origin",
      }).then(function () {
        var payloads = beforeItems.map(function (item) {
          var body = { id: item.variant_id, quantity: item.quantity };
          if (item.selling_plan) body.selling_plan = item.selling_plan;
          return body;
        });

        return nativeFetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({ items: payloads }),
        });
      });
    }).then(function () {
      document.dispatchEvent(new CustomEvent("cart:updated"));
    });
  }

  function guardAdd(runMutation) {
    if (isBypassed()) return runMutation();

    return fetchCart()
      .then(function (before) {
        var beforeItems = snapshotItems(before.items);
        return runMutation().then(function (result) {
          return fetchCart().then(function (after) {
            var beforeIds = {};
            beforeItems.forEach(function (item) {
              beforeIds[item.product_id] = true;
            });

            var unauthorized = (after.items || []).some(function (item) {
              return !beforeIds[item.product_id];
            });

            if (unauthorized) {
              return restoreSnapshot(beforeItems).then(function () {
                return result;
              });
            }

            return result;
          });
        });
      })
      .catch(function () {
        return runMutation();
      });
  }

  function guardMutation(runMutation) {
    if (isBypassed()) return runMutation();

    return fetchCart()
      .then(function (before) {
        var beforeItems = snapshotItems(before.items);
        return runMutation().then(function (result) {
          return fetchCart().then(function (after) {
            if (shouldRestore(beforeItems, after.items)) {
              return restoreSnapshot(beforeItems).then(function () {
                return result;
              });
            }
            return result;
          });
        });
      })
      .catch(function () {
        return runMutation();
      });
  }

  var nativeFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    var url =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
          ? input.url
          : "";

    if (isCartMutationUrl(url) && !isBypassed()) {
      return guardMutation(function () {
        return nativeFetch.apply(window, arguments);
      });
    }

    if (isCartClearUrl(url) && !isBypassed()) {
      return guardMutation(function () {
        return nativeFetch.apply(window, arguments);
      });
    }

    if (isCartAddUrl(url) && !isBypassed()) {
      return guardAdd(function () {
        return nativeFetch.apply(window, arguments);
      });
    }

    return nativeFetch.apply(window, arguments);
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__sfCartUrl = url;
    return xhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    var xhr = this;
    var url = xhr.__sfCartUrl || "";

    if (isCartMutationUrl(url) && !isBypassed()) {
      guardMutation(function () {
        return new Promise(function (resolve, reject) {
          xhr.addEventListener("load", function onLoad() {
            xhr.removeEventListener("error", onError);
            resolve();
          });
          xhr.addEventListener("error", onError);
          function onError() {
            xhr.removeEventListener("load", onLoad);
            reject(new Error("Cart XHR failed"));
          }
          xhrSend.call(xhr, body);
        });
      });
      return;
    }

    if (isCartClearUrl(url) && !isBypassed()) {
      guardMutation(function () {
        return new Promise(function (resolve, reject) {
          xhr.addEventListener("load", function onLoad() {
            xhr.removeEventListener("error", onError);
            resolve();
          });
          xhr.addEventListener("error", onError);
          function onError() {
            xhr.removeEventListener("load", onLoad);
            reject(new Error("Cart XHR failed"));
          }
          xhrSend.call(xhr, body);
        });
      });
      return;
    }

    if (isCartAddUrl(url) && !isBypassed()) {
      guardAdd(function () {
        return new Promise(function (resolve, reject) {
          xhr.addEventListener("load", function onLoad() {
            xhr.removeEventListener("error", onError);
            resolve();
          });
          xhr.addEventListener("error", onError);
          function onError() {
            xhr.removeEventListener("load", onLoad);
            reject(new Error("Cart XHR failed"));
          }
          xhrSend.call(xhr, body);
        });
      });
      return;
    }

    return xhrSend.apply(this, arguments);
  };

  window.sfCartGuard = {
    withThemeBypass: withThemeBypass,
    fetch: function (url, options) {
      return withThemeBypass(function () {
        return nativeFetch(url, options);
      });
    },
  };
})();
